import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { getVehicles } from "../../services/vehicleService";
import { getParkers } from "../../services/parkerService";
import { createBooking, getAvailability } from "../../services/bookingService";
import { getTariffs } from "../../services/tariffService";
import SuccessMessageModal from "../Reusable/SuccessMessageModal";
import WarningMessageModal from "../Reusable/WarningMessageModal";
import {
  isNightRange,
  clampToDayTwoHours,
  clampToNightBlock,
} from "../../utils/bookingTime";

const CreateBookingModal = ({ isOpen, onClose, suggestedBooking }) => {
  const [vehicles, setVehicles] = useState([]);
  const [parkers, setParkers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [bookingData, setBookingData] = useState({
      vehicle_id: "",
      parker_id: "",
      tariff_id_applied: "",
      date: "",
      start_time: "",
      end_time: "",
      kwh_consumed: "",
      source: "manual",
  });
  const [availability, setAvailability] = useState([]);
  const [wasAdjusted, setWasAdjusted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const placeId = sessionStorage.getItem("selectedPlaceId");
        const params = placeId ? { place_id: placeId } : {};
        const vehiclesResponse = await getVehicles(params);
        setVehicles(vehiclesResponse.data);
        
        const parkersResponse = await getParkers(params);
        setParkers(parkersResponse.data);
        const tariffsResponse = await getTariffs(params);
        setTariffs(tariffsResponse.data);
        } catch (error) {
            setModalMessage("Error al cargar datos. Inténtalo de nuevo.", error);
            setIsWarningOpen(true);
        }
        };
        fetchData();
        if (suggestedBooking) {
      const formatTime = (d) => new Date(d).toTimeString().slice(0, 5);
      const startDate = new Date(suggestedBooking.start_date);

      setBookingData({
        ...bookingData,
        ...suggestedBooking,
        date: startDate.toISOString().slice(0, 10),
        start_time: formatTime(startDate),
        end_time: formatTime(new Date(suggestedBooking.end_date)),
        source: 'suggested',
      });
    }
  }, [suggestedBooking]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (bookingData.date && bookingData.parker_id) {
        try {
          const selectedParker = parkers.find(p => p.parker_id === bookingData.parker_id);
          if (selectedParker) {
            const response = await getAvailability(bookingData.date, selectedParker.location);
            const locationAvailability = response.availability[selectedParker.location];
            if (locationAvailability) {
              setAvailability(locationAvailability[bookingData.date] || []);
            } else {
              setAvailability(Array.from({ length: 24 }, (_, i) => i)); // All available if no data
            }
          }
        } catch (error) {
          setModalMessage("Error al cargar la disponibilidad. Inténtalo de nuevo.", error);
          setIsWarningOpen(true);
          setAvailability([]); // Clear availability on error
        }
      }
    };
    fetchAvailability();
  }, [bookingData.date, bookingData.parker_id, parkers]);
  
  const handleChange = (e) => {
      const { name, value } = e.target;
    setBookingData((prev) => ({ ...prev, [name]: value, source: 'manual' }));
    setWasAdjusted(false);
  };

  const handleBlockChange = (e) => {
    const { value } = e.target;
    const [start_time, end_time] = value.split('-');

    setBookingData((prev) => ({
      ...prev,
      start_time,
      end_time,
      source: 'manual',
    }));
  };

  const handleSave = async () => {
      if (!bookingData.vehicle_id || !bookingData.parker_id || !bookingData.tariff_id_applied || !bookingData.date || !bookingData.start_time || !bookingData.end_time) {
          setModalMessage("Todos los campos marcados con * son obligatorios.");
          setIsWarningOpen(true);
          return;
      }
  
      const { date, start_time, end_time } = bookingData;

    // Construct ISO 8601 strings directly from the normalized state
    const start_date = new Date(`${date}T${start_time}`).toISOString();
    
    let end_date_obj = new Date(`${date}T${end_time}`);
    
    // Handle overnight case for end_date
    if (end_date_obj < new Date(`${date}T${start_time}`)) {
      end_date_obj.setDate(end_date_obj.getDate() + 1);
    }
    
    const end_date = end_date_obj.toISOString();

    const dataToSubmit = {
      vehicle_id: bookingData.vehicle_id,
      parker_id: bookingData.parker_id,
      tariff_id_applied: bookingData.tariff_id_applied,
      start_date,
      end_date,
      booking_date: bookingData.date,
      booking_time: bookingData.start_time,
      kwh_consumed: parseFloat(bookingData.kwh_consumed) || 0,
    };

    setIsLoading(true);
    try {
      await createBooking(dataToSubmit);
      setModalMessage("Reserva creada exitosamente.");
      setIsSuccessOpen(true);
    } catch (error) {
      const detail = error.response?.data;
      let errorMessage = "Error al crear la reserva. Inténtalo de nuevo.";
      if (detail) {
        errorMessage = detail;
      }
      setModalMessage(errorMessage);
      setIsWarningOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    onClose(true); // Indicar que se debe refrescar
  };

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Crear Nueva Reserva</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField select label="Vehículo *" name="vehicle_id" value={bookingData.vehicle_id} onChange={handleChange} fullWidth>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                    {vehicle.plate} - {vehicle.model}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Parqueadero *" name="parker_id" value={bookingData.parker_id} onChange={handleChange} fullWidth>
                {parkers.map((parker) => (
                  <MenuItem key={parker.parker_id} value={parker.parker_id}>
                    {parker.location} - {parker.charger_type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField select label="Tarifa *" name="tariff_id_applied" value={bookingData.tariff_id_applied} onChange={handleChange} fullWidth>
                    {tariffs.map((tariff) => (
                        <MenuItem key={tariff.tariff_id} value={tariff.tariff_id}>
                            {tariff.name} - ${tariff.price_per_kwh}/kWh
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="date"
                label="Fecha"
                type="date"
                value={bookingData.date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                name="time_block"
                label="Seleccione el bloque horario"
                value={bookingData.start_time && bookingData.end_time ? `${bookingData.start_time}-${bookingData.end_time}` : ''}
                onChange={handleBlockChange}
                fullWidth
              >
                {/* Day Slots */}
                {Array.from({ length: 12 }, (_, i) => {
                  const startHour = i * 2;
                  const endHour = startHour + 2;
                  const value = `${String(startHour).padStart(2, '0')}:00-${String(endHour).padStart(2, '0')}:00`;
                  const label = `${String(startHour).padStart(2, '0')}:00 - ${String(endHour).padStart(2, '0')}:00`;
                  const now = new Date();
                  const isToday = bookingData.date === now.toISOString().slice(0, 10);
                  const isPast = isToday && endHour <= now.getHours();
                  const isAvailable = availability.includes(startHour) && !isPast;
                  return <MenuItem key={value} value={value} disabled={!isAvailable}>{label}</MenuItem>;
                })}
                {/* Night Slot */}
                <MenuItem value="20:00-22:00" disabled={!(availability.includes(20) && availability.includes(21) && availability.includes(22) && availability.includes(23) && availability.includes(0) && availability.includes(1) && availability.includes(2) && availability.includes(3) && availability.includes(4) && availability.includes(5))}>20:00 - 22:00 (Turno Nocturno)</MenuItem>
                <MenuItem value="22:00-06:00" disabled={!(availability.includes(20) && availability.includes(21) && availability.includes(22) && availability.includes(23) && availability.includes(0) && availability.includes(1) && availability.includes(2) && availability.includes(3) && availability.includes(4) && availability.includes(5))}>22:00 - 06:00 (Turno Nocturno)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="kwh_consumed"
                label="Kilowatts Consumidos"
                type="number"
                value={bookingData.kwh_consumed}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>Guardar</Button>
      </DialogActions>
      <SuccessMessageModal isOpen={isSuccessOpen} message={modalMessage} onClose={handleSuccessClose} />
      <WarningMessageModal isOpen={isWarningOpen} message={modalMessage} onClose={() => setIsWarningOpen(false)} />
    </Dialog>
  );
};

export default CreateBookingModal;

